import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutGrid, Newspaper, Sparkles, Send, Globe, Mail, ArrowLeft,
  RefreshCw, AlertTriangle, Play, Copy, Download, Inbox, Check, Loader2,
} from "lucide-react";
import { api, API_BASE as API, formatApiError } from "@/lib/api";
import { toast as sonnerToast } from "sonner";
// Note: adapted from bundle's @/lib/api (Bearer/localStorage) to host's
// withCredentials cookie auth — re-apply if upgrading bundle.

const TABS = [
  ["overview", "Overview", LayoutGrid],
  ["discovery", "Discovery", Newspaper],
  ["content", "Content", Sparkles],
  ["publishing", "Publishing", Send],
  ["seo", "SEO Pages", Globe],
  ["newsletter", "Newsletter", Mail],
];

export default function MarketingDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [busy, setBusy] = useState(null);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        if (r.data?.role !== "admin") navigate("/login");
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const trigger = useCallback(async (job) => {
    setBusy(job);
    setToast(`Starting ${job}…`);
    try {
      const { data: j } = await api.post(`/marketing/admin/run?job=${job}`);
      if (j.jobId) {
        for (let i = 0; i < 150; i++) {
          await new Promise((res) => setTimeout(res, 2000));
          const { data: jr } = await api.get(`/marketing/admin/jobs/${j.jobId}`);
          if (jr.progress?.message) setToast(jr.progress.message);
          if (jr.status === "success") {
            setToast(`${job} done.`);
            break;
          }
          if (jr.status === "error") {
            setToast(`${job} failed: ${jr.error}`);
            break;
          }
        }
      } else if (j.error) {
        setToast(`${job} failed: ${j.error}`);
      } else {
        setToast(`${job} done.`);
      }
    } catch (e) {
      setToast(`${job} failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setBusy(null);
      setTimeout(() => setToast(""), 5000);
    }
  }, []);

  async function logout() {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    // setToken removed during merge — host uses httpOnly cookies cleared above
    navigate("/login");
  }

  return (
    <main className="min-h-screen bg-background text-foreground" data-testid="marketing-dashboard">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="container py-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                <Inbox className="w-4 h-4 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-semibold font-display">Marketing Engine</h1>
            </div>
          </div>
          <button onClick={logout} className="text-sm text-muted-foreground hover:text-foreground" data-testid="logout-button">
            Sign out
          </button>
        </div>
        <nav className="container flex gap-1 overflow-x-auto pb-1">
          {TABS.map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`tab-${key}`}
              className={`px-3 py-2 text-sm inline-flex items-center gap-2 border-b-2 -mb-px whitespace-nowrap ${
                tab === key
                  ? "border-[hsl(var(--ring))] text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <div className="container py-6">
        {tab === "overview" && <Overview trigger={trigger} busy={busy} />}
        {tab === "discovery" && <Discovery busy={busy} />}
        {tab === "content" && <Content busy={busy} />}
        {tab === "publishing" && <Publishing trigger={trigger} busy={busy} />}
        {tab === "seo" && <Seo busy={busy} />}
        {tab === "newsletter" && <Newsletter busy={busy} />}
      </div>

      {toast && (
        <div
          className="fixed bottom-6 right-6 bg-card border border-border rounded-lg shadow-lg px-4 py-3 text-sm flex items-center gap-2 max-w-md z-50"
          data-testid="job-toast"
        >
          <RefreshCw className="w-4 h-4 animate-spin" /> {toast}
        </div>
      )}
    </main>
  );
}

function RunBtn({ label, job, trigger, busy, icon: Icon = Play, variant = "default" }) {
  const isBusy = busy === job;
  const baseClass =
    variant === "primary"
      ? "bg-primary text-primary-foreground hover:opacity-90"
      : "bg-secondary text-secondary-foreground hover:bg-secondary/80";
  return (
    <button
      onClick={() => trigger(job)}
      disabled={!!busy}
      data-testid={`run-${job}-button`}
      className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md font-medium disabled:opacity-50 transition-opacity ${baseClass}`}
    >
      {isBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {isBusy ? `${label}…` : label}
    </button>
  );
}

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-bold mt-1">{value ?? "—"}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function Overview({ trigger, busy }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/marketing/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [busy]);
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold font-display">Pipeline status</h2>
        <div className="flex flex-wrap gap-2">
          <RunBtn label="Run full pipeline now" job="run_all" trigger={trigger} busy={busy} variant="primary" />
          <RunBtn label="Discover topics" job="discovery" trigger={trigger} busy={busy} />
          <RunBtn label="Score" job="scoring" trigger={trigger} busy={busy} />
          <RunBtn label="Generate" job="generate" trigger={trigger} busy={busy} />
          <RunBtn label="Build SEO pages" job="articles" trigger={trigger} busy={busy} />
          <RunBtn label="Generate images" job="images" trigger={trigger} busy={busy} icon={Sparkles} />
          <RunBtn label="Regenerate all images" job="regen_images" trigger={trigger} busy={busy} icon={RefreshCw} />
          <RunBtn label="Newsletter" job="newsletter" trigger={trigger} busy={busy} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard testid="stat-topics" label="Topics" value={stats?.topics_total} hint={`${stats?.topics_scored || 0} scored`} />
        <StatCard testid="stat-seo" label="SEO pages" value={stats?.seo_total} hint="published" />
        <StatCard testid="stat-scheduled" label="Scheduled" value={stats?.content_scheduled} hint="awaiting Buffer" />
        <StatCard testid="stat-published" label="Published" value={stats?.content_published} hint="shipped" />
      </div>
      <div className="bg-card border border-border rounded-lg">
        <div className="px-4 py-3 border-b border-border font-medium">Recent runs</div>
        <div className="divide-y divide-border text-sm" data-testid="recent-runs">
          {(stats?.recent_runs || []).map((r) => (
            <div key={r._id} className="px-4 py-2 flex items-center justify-between">
              <span>{r.run_type}</span>
              <span className="text-muted-foreground">
                {r.records_processed ?? 0} · {r.completed_at ? new Date(r.completed_at).toLocaleString() : ""}
              </span>
            </div>
          ))}
          {(stats?.recent_runs || []).length === 0 && (
            <div className="px-4 py-6 text-muted-foreground">No runs yet. Click “Run full pipeline now” to start.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function Discovery({ busy }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/marketing/topics").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [busy]);
  return (
    <div className="space-y-4" data-testid="discovery-tab">
      <h2 className="text-lg font-semibold font-display">Discovered topics</h2>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Topic</th>
              <th className="text-left p-3">Angle</th>
              <th className="text-left p-3">Audience</th>
              <th className="text-left p-3">Intent</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((t) => (
              <tr key={t._id} className="border-t border-border">
                <td className="p-3">
                  {t.topic}
                  <div className="text-xs text-muted-foreground mt-1">{t.target_keyword}</div>
                </td>
                <td className="p-3 uppercase text-xs tracking-wider text-[hsl(var(--ring))]">{t.angle}</td>
                <td className="p-3 text-muted-foreground">{t.audience}</td>
                <td className="p-3">{t.buyer_intent_score ?? "—"}</td>
                <td className="p-3 text-muted-foreground">{t.status}</td>
              </tr>
            ))}
            {(items || []).length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-muted-foreground text-center">
                  No topics yet — run “Discover topics”.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Content({ busy }) {
  const [items, setItems] = useState(null);
  const [platform, setPlatform] = useState("all");
  useEffect(() => {
    const q = platform === "all" ? "" : `?platform=${platform}`;
    api.get(`/marketing/content${q}`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [busy, platform]);
  function copy(s) {
    navigator.clipboard?.writeText(s);
  }
  return (
    <div className="space-y-4" data-testid="content-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold font-display">Generated content</h2>
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
          className="text-sm bg-background border border-input rounded-md px-2 py-1.5"
          data-testid="content-platform-filter"
        >
          {["all", "instagram", "facebook", "googlebusiness", "seo", "newsletter"].map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {(items || []).map((c) => (
          <div key={c._id} className="bg-card border border-border rounded-lg p-4">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--ring))] mb-1">{c.platform}</div>
            <div className="font-semibold mb-2">{c.headline}</div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-6">{c.caption}</p>
            <div className="mt-3 flex gap-2 text-xs">
              <button
                onClick={() =>
                  copy(`${c.caption}${c.hashtags?.length ? "\n\n" + c.hashtags.map((h) => "#" + h).join(" ") : ""}`)
                }
                className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md"
                data-testid={`copy-${c._id}`}
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
              {c.platform !== "seo" && c.platform !== "newsletter" && (
                <a
                  href={`${API}/og?id=${c._id}&template=${c.template || "how-to"}&download=1`}
                  className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md"
                >
                  <Download className="w-3 h-3" /> Image
                </a>
              )}
            </div>
          </div>
        ))}
        {(items || []).length === 0 && (
          <div className="text-muted-foreground bg-card border border-border rounded-lg p-6 lg:col-span-2">
            No content yet — run “Generate”.
          </div>
        )}
      </div>
    </div>
  );
}

function Publishing({ trigger, busy }) {
  const [data, setData] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  useEffect(() => {
    api.get("/marketing/publishing").then((r) => setData(r.data)).catch(() => {});
  }, [busy]);
  async function testBuffer() {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data: d } = await api.get("/marketing/buffer-audit");
      setDiag(d);
    } catch (e) {
      setDiag({ ok: false, reason: "fetch_failed", detail: e.message });
    } finally {
      setDiagBusy(false);
      api.get("/marketing/publishing").then((r) => setData(r.data)).catch(() => {});
    }
  }
  return (
    <div className="space-y-6" data-testid="publishing-tab">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold font-display">Buffer publishing</h2>
          {data && data.buffer_rate_limited && (
            <div data-testid="buffer-rate-limit-banner" className="text-xs text-red-700 mt-1 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 inline-flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5" />
              Buffer rate-limited (24h window).
              {data.buffer_rate_limit_reset_seconds > 0 && (
                <span>Resets in {Math.ceil(data.buffer_rate_limit_reset_seconds / 3600)}h{Math.floor((data.buffer_rate_limit_reset_seconds % 3600) / 60).toString().padStart(2, "0")}m.</span>
              )}
              <span className="opacity-60">All publish attempts will fail until reset.</span>
            </div>
          )}
          {data && !data.buffer_rate_limited &&
            (data.buffer_connected ? (
              <div className="text-xs text-emerald-600 mt-1" data-testid="buffer-status">
                Connected: {data.profiles?.map((p) => p.service).join(", ") || "no channels"}
              </div>
            ) : (
              <div className="text-xs text-amber-600 mt-1" data-testid="buffer-status">
                Buffer not connected{data.buffer_reason ? ` — ${data.buffer_reason}` : ""}.
                {data.buffer_error && (
                  <div className="mt-1 font-mono text-[10px] break-all max-w-2xl">{data.buffer_error}</div>
                )}
              </div>
            ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={testBuffer}
            disabled={diagBusy}
            data-testid="test-buffer-button"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50"
          >
            {diagBusy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {diagBusy ? "Testing…" : "Test Buffer connection"}
          </button>
          <button
            onClick={() => setConfirming("clear_queue")}
            disabled={!!busy}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-input bg-background hover:bg-accent disabled:opacity-50"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Clear Buffer queue
          </button>
          <button
            onClick={() => setConfirming("reimport")}
            disabled={!!busy}
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-primary text-primary-foreground disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" /> Clear &amp; re-import
          </button>
          <RunBtn label="Sync published" job="sync" trigger={trigger} busy={busy} icon={RefreshCw} />
          <RunBtn label="Dedupe drafts" job="dedupe" trigger={trigger} busy={busy} icon={AlertTriangle} />
          <RunBtn label="Retry failed" job="reimport" trigger={trigger} busy={busy} icon={RefreshCw} />
          <RunBtn label="Schedule next batch" job="schedule" trigger={trigger} busy={busy} icon={Send} variant="primary" />
        </div>
      </div>

      {diag && (
        <div
          data-testid="buffer-diag-result"
          className={`text-sm rounded-lg border ${
            diag.ok ? "border-emerald-300 bg-emerald-50/50" : "border-amber-300 bg-amber-50/50"
          }`}
        >
          <div className={`px-4 py-3 border-b flex items-center justify-between ${diag.ok ? "border-emerald-200" : "border-amber-200"}`}>
            <div className="font-semibold text-base">
              {diag.ok ? "✓ Buffer audit passed" : "⚠ Buffer audit found issues"}
            </div>
            <div className="text-xs text-muted-foreground">
              {(diag.checks || []).filter((c) => c.ok).length} / {(diag.checks || []).length} checks passed
            </div>
          </div>

          {Array.isArray(diag.checks) ? (
            <div className="divide-y divide-emerald-100/60">
              {diag.checks.map((c, i) => {
                const status = c.ok ? "✓" : (c.warning ? "⚠" : "✗");
                const color = c.ok ? "text-emerald-700" : (c.warning ? "text-amber-700" : "text-red-700");
                return (
                  <div key={i} className="px-4 py-2.5 flex items-start gap-3 text-xs">
                    <span className={`font-mono font-bold ${color}`}>{status}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold ${color}`}>{c.name}</div>
                      <div className="text-muted-foreground mt-0.5 break-words">{c.detail}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Legacy short diagnostic fallback
            <div className="p-3 text-xs">
              {diag.detail && (
                <pre className="whitespace-pre-wrap break-all font-mono bg-white/60 p-2 rounded">{diag.detail}</pre>
              )}
            </div>
          )}

          {(diag.warnings && diag.warnings.length > 0) && (
            <div className="px-4 py-3 border-t border-amber-200 bg-amber-100/50 text-xs">
              <div className="font-semibold text-amber-800 mb-1">Warnings</div>
              <ul className="list-disc pl-5 space-y-0.5 text-amber-900">
                {diag.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {(diag.next_steps && diag.next_steps.length > 0) && (
            <div className={`px-4 py-3 border-t text-xs ${diag.ok ? "border-emerald-200" : "border-amber-200"}`}>
              <div className="font-semibold mb-1">{diag.ok ? "Status" : "Next steps"}</div>
              <ul className="list-disc pl-5 space-y-0.5">
                {diag.next_steps.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {data?.schedule && (
        <div className="bg-card border border-border rounded-lg p-4 text-xs">
          <div className="font-semibold mb-2">Posting schedule · timezone {data.schedule.timezone}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {Object.entries(data.schedule.slots || {}).map(([plat, slots]) => (
              <div key={plat} className="border border-border rounded-md p-2">
                <div className="uppercase tracking-wider text-[10px] text-[hsl(var(--ring))] font-semibold">{plat}</div>
                <div className="font-mono mt-1">
                  {slots.map((s, i) => (
                    <div key={i}>
                      {String(s.hour).padStart(2, "0")}:{String(s.minute).padStart(2, "0")} ({i === 0 ? "slot 1" : "slot 2"})
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        {!data ? (
          <div className="p-4">Loading…</div>
        ) : (data.queue || []).length === 0 ? (
          <div className="p-4 text-muted-foreground">Nothing in the queue.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3">Platform</th>
                <th className="text-left p-3">Headline</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Scheduled</th>
              </tr>
            </thead>
            <tbody>
              {(data.queue || []).map((q) => (
                <tr key={q._id} className="border-t border-border">
                  <td className="p-3 uppercase tracking-wider text-[10px] text-[hsl(var(--ring))] font-semibold">{q.platform}</td>
                  <td className="p-3">{q.headline}</td>
                  <td className="p-3 text-muted-foreground">{q.status}</td>
                  <td className="p-3 text-muted-foreground">
                    {q.scheduled_at ? new Date(q.scheduled_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md shadow-xl">
            <div className="font-semibold text-lg mb-1">
              {confirming === "reimport" ? "Clear & re-import?" : "Clear Buffer queue?"}
            </div>
            <div className="text-sm text-muted-foreground mb-4">
              Permanently deletes every not-yet-published post on Buffer and resets local drafts.{" "}
              {confirming === "reimport" ? "Then schedules the next batch." : ""} Cannot be undone.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirming(null)} className="text-sm px-3 py-2 rounded-md border border-input">
                Cancel
              </button>
              <button
                onClick={() => {
                  const j = confirming;
                  setConfirming(null);
                  trigger(j);
                }}
                className="text-sm px-3 py-2 rounded-md bg-destructive text-destructive-foreground"
              >
                Yes, {confirming === "reimport" ? "clear & re-import" : "clear queue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Seo({ busy }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/marketing/articles").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [busy]);
  return (
    <div className="space-y-4" data-testid="seo-tab">
      <h2 className="text-lg font-semibold font-display">Published SEO pages</h2>
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {(items || []).length === 0 && (
          <div className="p-4 text-muted-foreground">No pages yet — click “Build SEO pages” on Overview.</div>
        )}
        {(items || []).map((p) => (
          <a
            key={p._id}
            href={`/blog/${p.slug}`}
            target="_blank"
            rel="noreferrer"
            data-testid={`seo-page-${p.slug}`}
            className="block border-t border-border first:border-t-0 px-4 py-3 hover:bg-accent"
          >
            <div className="font-semibold">{p.title}</div>
            <div className="text-xs text-muted-foreground mt-1">
              /blog/{p.slug} · {p.word_count} words · keyword: {p.target_keyword}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function NewsletterSendButton({ newsletter, onSent }) {
  const [busy, setBusy] = useState(false);
  const status = newsletter.status || "draft";
  const alreadySent = status === "sent" || status === "sent_with_errors";

  const send = async () => {
    if (busy || alreadySent) return;
    const subCount = await api
      .get("/marketing/admin/subscribers")
      .then((r) => r.data?.active ?? 0)
      .catch(() => 0);
    const ok = window.confirm(
      `Send this newsletter to ${subCount} active subscriber(s)?\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/marketing/admin/newsletter/send?newsletter_id=${newsletter._id}`,
      );
      if (data?.ok) {
        sonnerToast.success(`Sent to ${data.sent} subscriber(s)${data.failed ? `, ${data.failed} failed` : ""}.`);
        onSent && onSent();
      } else {
        sonnerToast.error(data?.error || "Failed to send.");
      }
    } catch (e) {
      sonnerToast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const dryRun = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/marketing/admin/newsletter/send?newsletter_id=${newsletter._id}&dry_run=true`,
      );
      sonnerToast.success(`Dry run — would send to ${data.sent} subscriber(s).`);
    } catch (e) {
      sonnerToast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (alreadySent) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Check className="h-3.5 w-3.5 text-emerald-500" />
        Sent {newsletter.sent_at ? new Date(newsletter.sent_at).toLocaleString() : ""}
        {typeof newsletter.sent_count === "number" && ` · ${newsletter.sent_count} subscribers`}
        {typeof newsletter.failed_count === "number" && newsletter.failed_count > 0 && ` · ${newsletter.failed_count} failed`}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={dryRun}
        disabled={busy}
        data-testid={`newsletter-dryrun-${newsletter._id}`}
        className="text-xs font-semibold border border-border bg-background hover:bg-muted px-3 py-1.5 rounded-md disabled:opacity-50"
      >
        Dry run
      </button>
      <button
        onClick={send}
        disabled={busy}
        data-testid={`newsletter-send-${newsletter._id}`}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#F26953] hover:bg-[#D85340] px-3 py-1.5 rounded-md disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
        {busy ? "Sending…" : "Send now"}
      </button>
    </div>
  );
}

function Newsletter({ busy }) {
  const [items, setItems] = useState(null);
  const [subscriberCount, setSubscriberCount] = useState(null);
  const refresh = () => {
    api.get("/marketing/newsletter").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/marketing/admin/subscribers").then((r) => setSubscriberCount(r.data?.active ?? 0)).catch(() => {});
  };
  useEffect(refresh, [busy]);
  return (
    <div className="space-y-4" data-testid="newsletter-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold font-display">Newsletter drafts</h2>
        <div className="text-xs text-muted-foreground" data-testid="newsletter-subscriber-count">
          {subscriberCount === null ? "…" : `${subscriberCount} active subscriber${subscriberCount === 1 ? "" : "s"}`}
        </div>
      </div>
      {(items || []).length === 0 && (
        <div className="p-4 text-muted-foreground bg-card border border-border rounded-lg">No drafts yet.</div>
      )}
      {(items || []).map((n) => (
        <div key={n._id} className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground uppercase tracking-wider">
              Week of {new Date(n.week_start).toLocaleDateString()}
            </div>
            <NewsletterSendButton newsletter={n} onSent={refresh} />
          </div>
          <p className="mt-2">{n.intro}</p>
          <div className="mt-4 space-y-3 text-sm">
            {(n.sections || []).map((s, i) => (
              <div key={i}>
                <div className="font-semibold">{s.headline}</div>
                <div className="text-muted-foreground whitespace-pre-wrap">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
